"use client";
import { DiagonalPattern } from "@/components/layout/diagonal-pattern";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { texts } from "@/lib/texts";
import { motion } from "framer-motion";
import { BrainCircuit, Code, Menu } from "lucide-react";
import { Link } from "next-view-transitions";
import Image from "next/image";
import React, { useEffect, useState } from "react";

const navbarTexts = texts.global.navbar;

interface MenuItem {
  title: string;
  url: string;
  description?: string;
  icon?: React.ReactNode;
  items?: MenuItem[];
}

interface NavbarProps {
  logo?: {
    url: string;
    src: string;
    alt: string;
    title: string;
  };
  menu?: MenuItem[];
  mobileExtraLinks?: {
    name: string;
    url: string;
  }[];
  auth?: {
    login: {
      text: string;
      url: string;
    };
    signup: {
      text: string;
      url: string;
    };
  };
}

const Navbar = ({
  logo = navbarTexts.logo,
  menu = navbarTexts.menu,
}: NavbarProps) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  return (
    <motion.section
      className="sticky top-0 z-50 border-b bg-background"
      initial={{ paddingTop: "2rem", paddingBottom: "2rem", height: "auto" }}
      animate={{
        paddingTop: scrolled ? "0.75rem" : "2rem",
        paddingBottom: scrolled ? "0.75rem" : "2rem",
        height: "auto",
      }}
      transition={{
        duration: 0.6,
        ease: [0.1, 0.4, 0.2, 1],
      }}
    >
      <div className="container mx-auto">
        {/* Desktop Menu */}
        <nav className="relative hidden flex-col items-center lg:flex">
          {/* Logo immer präsent, aber animiert */}
          <motion.div
            className="mb-2 overflow-hidden"
            animate={{
              height: scrolled ? "0px" : "96px",
              opacity: scrolled ? 0 : 1,
              scale: scrolled ? 0.8 : 1,
            }}
            transition={{
              duration: 0.6,
              ease: [0.1, 0.4, 0.2, 1],
            }}
          >
            <Link
              href={logo.url}
              className="flex items-center justify-center gap-2"
            >
              <Image
                src={logo.src}
                height={96}
                width={96}
                className="h-24 w-24"
                alt={logo.alt}
              />
            </Link>
          </motion.div>
          <div className="flex w-full items-center justify-center">
            <NavigationMenu>
              <NavigationMenuList>
                {menu.map((item) => (
                  <React.Fragment key={item.title}>
                    {renderMenuItem(item)}
                  </React.Fragment>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>
        </nav>
        {/* Mobile Menu */}
        <div className="block lg:hidden">
          <div className="relative flex items-center justify-center p-4">
            <div className="absolute left-1/2 -translate-x-1/2">
              <Link href={logo.url} className="flex items-center gap-2">
                <Image
                  height={80}
                  width={80}
                  src={logo.src}
                  className="w-20"
                  alt={logo.alt}
                />
              </Link>
            </div>
            <div className="absolute top-1/2 right-0 -translate-y-1/2">
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={navbarTexts.mobileMenu.openLabel}
                    className="h-12 w-12"
                  >
                    <Menu className="size-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent className="overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>
                      <SheetClose asChild>
                        <Link
                          href={logo.url}
                          className="flex items-center gap-2"
                        >
                          <Image
                            height={80}
                            width={80}
                            src={logo.src}
                            className="mx-auto w-16"
                            alt={logo.alt}
                          />
                        </Link>
                      </SheetClose>
                    </SheetTitle>
                    <SheetClose
                      aria-label={navbarTexts.mobileMenu.closeLabel}
                    ></SheetClose>
                  </SheetHeader>
                  <div className="flex flex-col items-start gap-6 p-4">
                    <div className="flex w-full flex-col gap-4 text-left">
                      {menu.map((item) => (
                        <React.Fragment key={item.title}>
                          {renderMobileMenuItem(item)}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

const renderMenuItem = (item: MenuItem) => {
  if (item.items) {
    return (
      <NavigationMenuItem key={item.title} className="text-muted-foreground">
        <NavigationMenuTrigger>{item.title}</NavigationMenuTrigger>
        <NavigationMenuContent>
          <div className="w-[500px] overflow-hidden bg-background">
            {/* Content */}
            <div className="relative grid grid-cols-1 gap-2 p-4">
              {item.items.map((subItem) => (
                <NavigationMenuLink asChild key={subItem.title}>
                  <SubMenuLink item={subItem} />
                </NavigationMenuLink>
              ))}
            </div>
          </div>
        </NavigationMenuContent>
      </NavigationMenuItem>
    );
  }

  return (
    <NavigationMenuItem key={item.title}>
      <div className="inline-flex">
        <Link
          className="group relative inline-flex h-10 w-max items-center justify-center px-4 py-2 text-sm font-medium text-muted-foreground transition-colors after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all after:duration-300 hover:text-primary hover:after:w-full"
          href={item.url}
        >
          {item.title}
        </Link>
      </div>
    </NavigationMenuItem>
  );
};

const renderMobileMenuItem = (item: MenuItem) => {
  if (item.items) {
    return (
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem
          key={item.title}
          value={item.title}
          className="border-b-0"
        >
          <AccordionTrigger className="text-md justify-start py-0 font-semibold hover:no-underline">
            {item.title}
          </AccordionTrigger>
          <AccordionContent className="mt-2">
            <div className="flex flex-col items-start">
              {item.items.map((subItem) => (
                <SheetClose asChild key={subItem.title}>
                  <Link
                    href={subItem.url}
                    className="text-md my-1 block w-full text-left font-medium"
                  >
                    {subItem.title}
                  </Link>
                </SheetClose>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <SheetClose asChild>
      <Link
        href={item.url}
        className="text-md block w-full text-left font-semibold"
      >
        {item.title}
      </Link>
    </SheetClose>
  );
};

const SubMenuLink = ({ item }: { item: MenuItem }) => {
  return (
    <Link
      className="group relative block overflow-hidden border border-primary/30 bg-background/80 backdrop-blur-[2px]"
      href={item.url}
    >
      <div className="flex h-full items-start gap-4 p-4 transition-colors hover:bg-primary/5">
        <div className="flex h-full items-center justify-center">
          <div className="relative flex h-12 w-12 items-center justify-center overflow-hidden border border-primary/30">
            {/* Icon Background Pattern */}
            <div className="absolute inset-0">
              <DiagonalPattern
                className="h-full border-none"
                patternOpacity={0.07}
                patternColor="hsl(var(--primary))"
              />
            </div>

            {/* Icon Border Accents */}
            <div className="absolute top-0 left-0 h-3 w-3 border-t border-l border-primary/70" />
            <div className="absolute top-0 right-0 h-3 w-3 border-t border-r border-primary/70" />
            <div className="absolute bottom-0 left-0 h-3 w-3 border-b border-l border-primary/70" />
            <div className="absolute right-0 bottom-0 h-3 w-3 border-r border-b border-primary/70" />

            {/* Icon Container */}
            <div className="relative z-10 flex h-8 w-8 items-center justify-center bg-background/70 text-primary">
              {item.icon ||
                (item.title === "KI-Beratung" ? (
                  <BrainCircuit className="size-5 shrink-0" />
                ) : (
                  <Code className="size-5 shrink-0" />
                ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-sm font-semibold text-foreground">
            {item.title}
          </div>
          {item.description && (
            <p className="text-sm leading-snug text-muted-foreground">
              {item.description}
            </p>
          )}
        </div>
      </div>

      {/* Bottom border animation */}
      <div className="absolute bottom-0 left-0 h-1 w-0 bg-primary transition-all duration-300 group-hover:w-full"></div>

      {/* Side Pattern */}
      <div className="absolute top-0 right-0 h-full w-1 opacity-20">
        <DiagonalPattern
          className="h-full border-none"
          patternColor="hsl(var(--primary))"
        />
      </div>
    </Link>
  );
};

export { Navbar };
